/**
 * Page /invitation/:token — Livraison 6 (Bloc C).
 *
 * Page publique (sans auth) accédée depuis le lien magique reçu par email.
 * Flow :
 *   1. Au montage : appelle GET /api/invitations/:token/verify
 *      → si invalid : affiche l'erreur (expiré / révoqué / déjà accepté / inconnu)
 *      → si valid : pré-remplit email + tenant + rôle (read-only)
 *   2. L'invité saisit prénom + nom + mdp + CGU/PC
 *   3. POST /api/invitations/:token/accept → user créé, redirige vers /connexion
 *
 * Refonte R12 (mai 2026) :
 *   - Topbar glass avec halo tech (cohérent R11)
 *   - Card glass principale form avec 4 états :
 *     - Vérification (loading spinner)
 *     - Erreur (LogoMark AlertCircle urgent + reason title + body + retour accueil)
 *     - Succès (LogoMark CheckCircle2 success + redirect 3s vers /connexion)
 *     - Form (LogoMark Mail tech + identité tenant + form 4 champs + CGU/PC accent-tech + bandeau info)
 *
 * Logique métier 100% préservée :
 *   - api.invitations.verify(token) au mount → 4 reasons gérés
 *   - api.invitations.accept(token, {prenom, nom, password, cguAccepted, pcAccepted})
 *   - Validation : password === confirmPassword + cgu + pc
 *   - Sur succès : setSuccess(true) + setTimeout 3000ms → navigate('/connexion')
 *   - ROLE_LABEL + ERROR_MESSAGES inchangés
 */

import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import {
  Card, Button, Input, Field, Alert, Badge, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { VpcMairieIcon } from '@/components/ui/VpcMairieIcon';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  Mail, ShieldCheck, AlertCircle, CheckCircle2, ArrowLeft,
} from 'lucide-react';
import type { VerifyInvitationResponse } from '@roronoa-collectivites/shared-types';

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super administrateur',
  admin: 'Administrateur',
  manager: 'Chef de service',
  agent: 'Agent',
  administre: 'Administré',
};

const ERROR_MESSAGES: Record<NonNullable<VerifyInvitationResponse['reason']>, { title: string; body: string }> = {
  expired: {
    title: 'Lien expiré',
    body: 'Ce lien d\'invitation a dépassé sa date de validité (7 jours). Demandez à la personne qui vous a invité de vous renvoyer une nouvelle invitation.',
  },
  revoked: {
    title: 'Invitation révoquée',
    body: 'Cette invitation a été annulée par l\'administrateur de la collectivité. Contactez-le pour qu\'il vous envoie une nouvelle invitation si nécessaire.',
  },
  accepted: {
    title: 'Invitation déjà acceptée',
    body: 'Cette invitation a déjà été utilisée pour créer un compte. Si vous êtes le destinataire, connectez-vous directement avec votre email et votre mot de passe.',
  },
  not_found: {
    title: 'Lien inconnu',
    body: 'Ce lien d\'invitation n\'existe pas ou a été altéré. Vérifiez que vous avez bien copié l\'URL complète depuis votre email.',
  },
};

const TOPBAR_BG = {
  backgroundImage:
    'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
    'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
};

export function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [verifyResult, setVerifyResult] = useState<VerifyInvitationResponse | null>(null);

  // Form state
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [cguAccepted, setCguAccepted] = useState(false);
  const [pcAccepted, setPcAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    async function verify() {
      if (!token) {
        setVerifyResult({ valid: false, reason: 'not_found' });
        setVerifying(false);
        return;
      }
      try {
        const res = await api.invitations.verify(token);
        setVerifyResult(res);
      } catch {
        setVerifyResult({ valid: false, reason: 'not_found' });
      } finally {
        setVerifying(false);
      }
    }
    void verify();
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (password !== confirmPassword) {
      setSubmitError('La confirmation ne correspond pas au mot de passe.');
      return;
    }
    if (!cguAccepted || !pcAccepted) {
      setSubmitError('Vous devez accepter les CGU et la politique de confidentialité.');
      return;
    }
    setSubmitting(true);
    try {
      await api.invitations.accept(token!, {
        prenom: prenom.trim(),
        nom: nom.trim(),
        password,
        cguAccepted: true,
        pcAccepted: true,
      });
      setSuccess(true);
      setTimeout(() => navigate('/connexion'), 3000);
    } catch (err) {
      setSubmitError(err instanceof ApiException ? err.payload.error || 'Erreur' : 'Erreur réseau');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen text-ink">
      {/* Topbar glass */}
      <header
        className="sticky top-0 z-20 flex items-center gap-3 border-b border-border px-4 py-3 backdrop-blur-glass sm:px-6"
        style={TOPBAR_BG}
      >
        <LogoMark icon={VpcMairieIcon} accent="tech" size="md" aria-label="Vision Pro Collectivité" />
        <div className="min-w-0 flex-1">
          <SectionEyebrow>Invitation magic-link</SectionEyebrow>
          <div className="font-display text-sm font-semibold tracking-r-snug text-ink truncate">
            Vision Pro Collectivité
          </div>
        </div>
        <ThemeToggle />
      </header>

      <main className="mx-auto max-w-xl px-4 py-8 sm:py-12 sm:px-6 animate-fade-in">
        {/* État 1 : Vérification */}
        {verifying && (
          <Card variant="glass" padded>
            <div className="py-12 text-center">
              <div
                className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-tech border-t-transparent"
                role="status"
                aria-live="polite"
              />
              <p className="mt-4 text-sm text-ink-muted">Vérification de votre invitation…</p>
            </div>
          </Card>
        )}

        {/* État 2 : Erreur (4 reasons) */}
        {!verifying && verifyResult && !verifyResult.valid && (
          <Card variant="glass" padded>
            <div className="py-8 text-center">
              <LogoMark icon={AlertCircle} accent="urgent" size="lg" className="mx-auto" />
              <SectionEyebrow as="div" className="mt-3 text-urgent">Invitation invalide</SectionEyebrow>
              <h1 className="mt-1 font-display text-xl font-semibold tracking-r-snug text-ink">
                {ERROR_MESSAGES[verifyResult.reason ?? 'not_found'].title}
              </h1>
              <p className="mx-auto mt-2 max-w-md text-sm text-ink-muted leading-relaxed">
                {ERROR_MESSAGES[verifyResult.reason ?? 'not_found'].body}
              </p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-1 rounded-r-md px-1 text-sm text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour à l'accueil
              </Link>
            </div>
          </Card>
        )}

        {/* État 3 : Succès */}
        {!verifying && verifyResult?.valid && success && (
          <Card variant="glass" padded>
            <div className="py-8 text-center">
              <LogoMark icon={CheckCircle2} accent="success" size="lg" className="mx-auto" />
              <SectionEyebrow as="div" className="mt-3 text-success">Bienvenue</SectionEyebrow>
              <h1 className="mt-1 font-display text-xl font-semibold tracking-r-snug text-ink">
                Compte créé !
              </h1>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                Bienvenue dans <strong className="text-ink">{verifyResult.tenantNom}</strong>. Vous allez être
                redirigé vers la page de connexion…
              </p>
              <Link
                to="/connexion"
                className="mt-4 inline-flex items-center gap-1 rounded-r-md px-1 text-sm text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                Aller à la connexion maintenant
              </Link>
            </div>
          </Card>
        )}

        {/* État 4 : Form invitation valide */}
        {!verifying && verifyResult?.valid && !success && (
          <Card variant="glass" padded={false}>
            <div className="border-b border-border p-5 sm:p-6">
              <div className="flex items-center gap-3">
                <LogoMark icon={Mail} accent="tech" size="md" />
                <div className="min-w-0 flex-1">
                  <SectionEyebrow as="div">Invitation</SectionEyebrow>
                  <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink">
                    Vous avez été invité·e
                  </h1>
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
                    Rejoignez <strong className="text-ink">{verifyResult.tenantNom}</strong> en tant que{' '}
                    <Badge variant="tech" size="sm">
                      {ROLE_LABEL[verifyResult.role ?? ''] ?? verifyResult.role}
                    </Badge>
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={onSubmit}>
              <div className="space-y-4 p-5 sm:p-6">
                {submitError && <Alert variant="error" icon={AlertCircle}>{submitError}</Alert>}

                <Field label="Email" htmlFor="acc-email" hint="Email indiqué par l'inviteur, non modifiable.">
                  <Input id="acc-email" value={verifyResult.email ?? ''} readOnly disabled />
                </Field>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Prénom" htmlFor="acc-prenom" required>
                    <Input
                      id="acc-prenom"
                      value={prenom}
                      onChange={(e) => setPrenom(e.target.value)}
                      required
                      autoComplete="given-name"
                    />
                  </Field>
                  <Field label="Nom" htmlFor="acc-nom" required>
                    <Input
                      id="acc-nom"
                      value={nom}
                      onChange={(e) => setNom(e.target.value)}
                      required
                      autoComplete="family-name"
                    />
                  </Field>
                </div>

                <Field
                  label="Mot de passe"
                  htmlFor="acc-password"
                  hint="Minimum 12 caractères. Évitez les mots qui font partie de votre identité (prénom, nom, email)."
                  required
                >
                  <Input
                    id="acc-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </Field>
                <Field label="Confirmer le mot de passe" htmlFor="acc-confirm" required>
                  <Input
                    id="acc-confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </Field>

                <div className="space-y-2 border-t border-border pt-4">
                  <label className="flex items-start gap-2 text-sm text-ink cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cguAccepted}
                      onChange={(e) => setCguAccepted(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-tech"
                      required
                    />
                    <span>
                      J'accepte les{' '}
                      <Link
                        to="/legal/cgu"
                        target="_blank"
                        className="rounded-r-md px-0.5 text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                      >
                        conditions générales d'utilisation
                      </Link>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-ink cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pcAccepted}
                      onChange={(e) => setPcAccepted(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-tech"
                      required
                    />
                    <span>
                      J'accepte la{' '}
                      <Link
                        to="/legal/politique-confidentialite"
                        target="_blank"
                        className="rounded-r-md px-0.5 text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                      >
                        politique de confidentialité
                      </Link>
                    </span>
                  </label>
                </div>

                <div className="rounded-r-lg border border-tech/30 bg-tech/5 backdrop-blur-glass p-3 text-xs text-ink-muted leading-relaxed">
                  <ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-tech-fg align-text-bottom" aria-hidden="true" />
                  Votre email est automatiquement vérifié du fait que vous avez cliqué le lien magique.
                  Vous pourrez vous connecter directement après création.
                </div>
              </div>
              <div className="flex justify-end border-t border-border bg-surface/30 backdrop-blur-glass p-4">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={
                    submitting || !prenom || !nom || !password || !confirmPassword || !cguAccepted || !pcAccepted
                  }
                >
                  {submitting ? 'Création…' : 'Créer mon compte'}
                </Button>
              </div>
            </form>
          </Card>
        )}
      </main>
    </div>
  );
}
